export interface ParkingLinkCandidate {
  sourceId: string;
  targetId: string;
  distance: number;
}

/**
 * Nearest-first allocation of parking spaces to cameras (center-to-center distance).
 * 1) Every camera without a saved link gets its nearest free space, ignoring the radius.
 * 2) Remaining spaces inside the radius go to the nearest camera, up to its capacity.
 * Each space is linked to one camera only.
 */
export function nearestParkingLinks(
  candidates: ParkingLinkCandidate[],
  existingCounts: Map<string, number>,
  capacities: Map<string, number | null>,
  maxRadius: number,
): Map<string, string[]> {
  const linked = new Map<string, string[]>();
  const used = new Set<string>();
  const sorted = candidates
    .filter((p) => Number.isFinite(p.distance))
    .sort(
      (a, b) =>
        a.distance - b.distance ||
        a.sourceId.localeCompare(b.sourceId) ||
        a.targetId.localeCompare(b.targetId),
    );
  const hasRoom = (id: string) => {
    const cap = capacities.get(id);
    return cap == null || (linked.get(id)?.length || 0) < cap;
  };
  const assign = (p: ParkingLinkCandidate) => {
    linked.set(p.sourceId, [...(linked.get(p.sourceId) || []), p.targetId]);
    used.add(p.targetId);
  };
  for (const p of sorted) {
    if (used.has(p.targetId) || linked.has(p.sourceId) || existingCounts.get(p.sourceId)) continue;
    if (hasRoom(p.sourceId)) assign(p);
  }
  for (const p of sorted) {
    if (p.distance > maxRadius) break;
    if (!used.has(p.targetId) && hasRoom(p.sourceId)) assign(p);
  }
  return linked;
}
